import CryptoJS from "crypto-js";

/**
 * Remove white space from strings
 * @param {String} str - String to remove white space from
 * @returns {String} - String without white space
 */
export const removeWhiteSpace = (str) => {
    if (typeof str !== "string" || str.length === 0) return "";
    return str?.replace(/[\s\t]/g, "");
};

/**
 * Remove space from the start and end of a strings
 * @param {String} str - String to trim
 * @returns {String} - String trimed
 */
export const stringTrim = (str) => {
    if (typeof str !== "string" || str.length === 0) return "";
    return str.trim();
};

/**
 * Remove (/,-,_) characters , white space, and capitalize strings
 * @param {String} str - String to clean and capitalize
 * @returns {String} - Cleaned and capitalized string
 */
export const cleanAndCapitalize = (str) => {
    if (typeof str !== "string" || str.length === 0) return "";
    return str.replace(/[/\-_ \s\t]/g, "").toUpperCase();
};

/**
 * Capitalize the first letter of the string
 * @param {String} str - String to  capitalize
 * @returns {String} - capitalized string
 */
export const capitalizeFirstLetter = (str) => {
    if (typeof str !== "string" || str.length === 0) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Filter object keys for keys that matches the predicate condition
 */
export const filterObjectByKey = (predicate, obj) => {
    const filteredKeys = Object.keys(obj).filter(predicate);
    const filteredData = Object.fromEntries(
        filteredKeys.map((key) => [key, obj[key]]),
    );
    return filteredData;
};

/**
 * Decrypt data using intKey
 * @param {String} inputdata - The data to decrypt
 * @param {String} intKey - The key to decrypt the data
 * @returns {String} - The decrypted data
 */
export const decryptor = (inputdata, intKey) => {
    if (!inputdata || !intKey) return;
    const extractedKey =
        intKey.indexOf("|") !== -1
            ? intKey.slice(0, intKey.indexOf("|"))
            : null;
    const extractedIv =
        intKey.indexOf("|") !== -1
            ? intKey.slice(intKey.indexOf("|") + 1)
            : null;

    if (extractedKey && extractedIv) {
        const key = CryptoJS.MD5(extractedKey).toString();
        const iv = CryptoJS.MD5(extractedIv).toString();

        const decryptString = (toDecrypt) => {
            const decrypted = CryptoJS.AES.decrypt(
                toDecrypt,
                CryptoJS.enc.Hex.parse(key),
                {
                    iv: CryptoJS.enc.Hex.parse(iv),
                },
            ).toString(CryptoJS.enc.Utf8);
            return decrypted;
        };

        return decryptString(inputdata);
    }
};

/**
 *
 * Encrypt data using intKey
 * @param {String} inputdata - The data to encrypt
 * @param {String} intKey - The key to encrypt the data
 * @returns {String} - The encrypted data
 */
export const encryptor = (inputdata, intKey) => {
    if (!inputdata || !intKey) return;
    const extractedKey =
        intKey.indexOf("|") !== -1
            ? intKey.slice(0, intKey.indexOf("|"))
            : null;
    const extractedIv =
        intKey.indexOf("|") !== -1
            ? intKey.slice(intKey.indexOf("|") + 1)
            : null;

    if (extractedKey && extractedIv) {
        const key = CryptoJS.MD5(extractedKey).toString();
        const iv = CryptoJS.MD5(extractedIv).toString();

        const encryptString = (toEncrypt) => {
            const encrypted = CryptoJS.AES.encrypt(
                toEncrypt,
                CryptoJS.enc.Hex.parse(key),
                {
                    iv: CryptoJS.enc.Hex.parse(iv),
                },
            ).toString();
            return encrypted;
        };

        return encryptString(inputdata);
    }
};

/**
 * create intKey for encryption and decryption
 * @param {Integer} segmentLengths The segments ot the key and the length of each segment [10,15] 2 segments the fist is 10 symbols and the second is 15 symbols
 * @returns String the intKey
 */
export const generateIntKey = (segmentLengths) => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

    const generateKey = () => {
        let key = "";

        segmentLengths.forEach((length, index) => {
            if (index > 0) {
                key += "-";
            }

            let segment = "";
            for (let i = 0; i < length; i++) {
                segment += chars.charAt(
                    Math.floor(Math.random() * chars.length),
                );
            }

            key += segment;
        });

        return key;
    };

    const firstKey = generateKey();
    const secondKey = generateKey();

    return `${firstKey}|${secondKey}`;
};

/**
 * Format the date to the client machine format
 * @param {Date|string|number} date - The date to format
 * @returns {Date} - The formatted date
 */
export const formatDateToLocalDate = (date, short) => {
    if (date) {
        const currentDate = new Date(date);
        if (!(currentDate instanceof Date) || isNaN(currentDate.getTime())) {
            throw new Error("Invalid date");
        }
        const locale = navigator.language;
        const dateFormatter = new Intl.DateTimeFormat(locale, {
            year: short ? "2-digit" : "numeric",
            month: "2-digit",
            day: "2-digit",
        });

        return dateFormatter.format(currentDate);
    }
};

/**
 * Format the time to the client machine format
 * @param {Date|string|number} date - The date to format
 * @returns {string} - The formatted time
 */
export const formatTimeToLocalTime = (date) => {
    if (date) {
        const currentDate = new Date(date);
        if (!(currentDate instanceof Date) || isNaN(currentDate.getTime())) {
            throw new Error("Invalid date");
        }
        const locale = navigator.language;
        const timeFormatter = new Intl.DateTimeFormat(locale, {
            hour: "2-digit",
            minute: "2-digit",

            hour12: false,
        });

        return timeFormatter.format(currentDate);
    }
};

/**
 * Convert hex color to rgb
 * @param {String} hex - The hex color value to convert to rgb
 * @returns {String} - The rgb color value
 */
export const hexToRGB = (hex) => {
    if (!hex) return;
    try {
        hex = hex.replace("#", "");
        if (hex.length === 3) {
            hex = hex
                .split("")
                .map(function (hex) {
                    return hex + hex;
                })
                .join("");
        }

        const bigint = parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;

        return `${r}, ${g}, ${b}`;
    } catch (error) {
        console.log(error);
    }
};

/**
 * Decode a JWT and check whether it is expired (or about to expire).
 * @param {string} token - the JWT access token
 * @param {number} skewSeconds - treat as expired this many seconds early (clock skew buffer)
 * @returns {boolean} true if missing, malformed, or expired
 */
export const isTokenExpired = (token, skewSeconds = 30) => {
    if (!token) return true;
    try {
        // JWT uses base64url, so convert it before atob
        const base64 = token
            .split(".")[1]
            .replace(/-/g, "+")
            .replace(/_/g, "/");
        const payload = JSON.parse(atob(base64));
        if (!payload?.exp) return true; // no exp claim → don't trust it
        return payload.exp <= Date.now() / 1000 + skewSeconds;
    } catch {
        return true; // malformed → treat as expired
    }
};

/**
 * Change favicon for the page
 * @param {URL} url - The url of the favicon
 */
export const changeFavicon = (url) => {
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
        link = document.createElement("link");
        link.rel = "shortcut icon";
        document.getElementsByTagName("head")[0].appendChild(link);
    }
    link.href = url;
};

/**
 * Penalty and amount calculation
 */
/**
 *
 * @param {Float} balance - The balance of the ticket to calculate the penalty from
 * @param {Float} amount - The amount of the ticket to calculate the total from
 * @returns {Object} - The penalty and total values
 */
export const calculateValues = (balance, amount, transactions) => {
    /**
     * The sum of tax for all the items in the main transaction (tickets, services).
     * Used to show the tax in the payments section.
     * @returns {Float} - the sam of all the tax in the main transaction
     */
    const getSumAllTax = (transactions) => {
        let total = 0;

        transactions &&
            transactions.length &&
            transactions.forEach((item) => {
                total += item?.taxAmount ? item?.taxAmount : 0;
            });

        return total;
    };

    const tax = getSumAllTax(transactions);

    const penalty = parseFloat(balance) - parseFloat(amount) - parseFloat(tax);
    const total = parseFloat(balance);

    return {
        penalty: penalty ? Math.abs(penalty) : 0,
        total: total ? total : 0,
        tax: tax ? tax : 0,
    };
};

// Not used
// export const calculateCartItems = (obj) => {
//     let itemsQuantity = 0;
//     if (obj && obj?.length) {
//         obj.forEach((item) => (itemsQuantity += item?.quantity));
//     }

//     return itemsQuantity;
// };

/**
 * Calculate the amount and administrative values for the selected tickets
 */
/**
 *
 * @param {Array} selectedTickets - (Array of Objects) The selected tickets to calculate the amount and administrative values from
 * @param {Object} feeDetails - (Object) The current clients fee details
 * @returns {Object} - The amount and administrative values
 */

export const calculatePayValues = (
    selectedTickets,
    selectedInstalments,
    feeDetails,
) => {
    const thresholdValue = 5;
    const calculateValuesPerType = (selectedTickets, feeDetails) => {
        const amount = selectedTickets.reduce(
            (acc, ticket) => acc + parseFloat(ticket?.amount),
            0,
        );

        const percent = amount
            ? amount * (feeDetails?.percentageCosts).toFixed(2)
            : 0;
        // console.log("percent: ", percent);
        // console.log(
        //     "(feeDetails?.percentageCosts).toFixed(2): ",
        //     (feeDetails?.percentageCosts).toFixed(2)
        // );
        return { amount, percent };
    };
    const TicketsPayment = calculateValuesPerType(selectedTickets, feeDetails);
    const instalmentsPayment = calculateValuesPerType(
        selectedInstalments,
        feeDetails,
    );

    // console.log("TicketsPayment: ", TicketsPayment);
    // console.log("instalmentsPayment: ", instalmentsPayment);

    const administrative =
        selectedTickets.length || selectedInstalments.length
            ? feeDetails?.fixedCosts +
              TicketsPayment?.percent +
              instalmentsPayment?.percent
            : 0;
    // console.log("feeDetails?.fixedCosts: ", feeDetails?.fixedCosts);
    // console.log("TicketsPayment?.percent: ", TicketsPayment?.percent);
    // console.log("instalmentsPayment?.percent: ", instalmentsPayment?.percent);
    // console.log("real value administrative: ", administrative);

    const amount = TicketsPayment?.amount + instalmentsPayment?.amount;
    // console.log("full amount: ", amount);

    return {
        administrative:
            administrative < thresholdValue
                ? Math.ceil(administrative)
                : Math.round(administrative),
        amount: Math.round(amount),
    };
};

// export const calculatePayValues = (selectedTickets, feeDetails) => {
//     const amount = selectedTickets.reduce(
//         (acc, ticket) => acc + parseFloat(ticket?.amount),
//         0
//     );

//     // const fixed = parseFloat(feeDetails?.fixedCosts)
//     const fixed = parseFloat(feeDetails?.fixedCosts);

//     const percent = amount
//         ? amount * (feeDetails?.percentageCosts).toFixed(2)
//         : 0;

//     // const administrative = fixed + percent;
//     const administrative = Math.round(fixed + percent);

//     return { amount, percent };
// };

/**
 * Count the quantity of proporties in an object
 * @param {Object} obj - The object to count the properties from
 * @returns {Integer} - The number of properties in the object
 */
export const countObjectProporties = (obj) => {
    return Object.keys(obj).length;
};

/**
 * Delete empty proporties from an object
 * @param {Object} obj - The object to delete the empty properties from
 * @returns {Object} - An object without any empty properties
 */
export const deleteObjectEmptyProporties = (obj) => {
    return Object.keys(obj).reduce((newObj, key) => {
        if (obj[key] !== null && obj[key] !== undefined && obj[key] !== "") {
            newObj[key] = obj[key];
        }
        return newObj;
    }, {});
};

/**
 * Find the max id value from an array of objects that contains an id property
 * @param {Array} array - (Array of Objects) The array to get the max id from
 * @returns {Integer} - The max id from the array
 */
export const getMaxId = (array) => {
    if (!array || array.length < 1) return 0;

    return array.reduce((maxId, item) => Math.max(maxId, item.id), -Infinity);
};

/**
 * Convert a string to camel case
 * @param {String} string - The string to convert to camel case
 * @returns {String} - The string in camel case
 */
export const toCamelCase = (string) => {
    return string
        .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) =>
            index === 0 ? match.toLowerCase() : match.toUpperCase(),
        )
        .replace(/\s+/g, "");
};

/**
 * Return the instalment status name
 * @param {Integer} - The instalment status
 * @returns {String} - The instalment status name
 */
export const getInstalmentStatus = (status) => {
    switch (status) {
        case 0:
            return "overdue";
        case 1:
            return "due";
        case 2:
            return "upcoming";
    }
};

/**
 * Return the instalment status color
 * @param {Integer} - The instalment status
 * @returns {String} - The instalment status color
 */
export const getInstalmentColor = (status) => {
    switch (status) {
        case 0:
            return "#ff0000";
        case 1:
            return "#ffdc00";
        case 2:
            return "";
    }
};
